import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMapEvents, useMap } from 'react-leaflet';
import { MapPin, Search, ShoppingBag, Truck, Crosshair, GraduationCap, Home, Award, CalendarDays, Stethoscope } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import SaveSearchButton from '../components/SaveSearchButton';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { resolveApi, resolveUrl, resolveTab, injectAds } from "@/utils/adUtil";
import AdSlot from "@/components/AdSlot";

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
	iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
	shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const pinIcon = new L.Icon({
	iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
	iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
	shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
	iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
});

const adIcon = new L.Icon({
	iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
	iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
	shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
	iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
});

function ClickHandler({ onMapClick }) {
	useMapEvents({ click(e) { onMapClick(e.latlng); } });
	return null;
}

function AutoZoom({ pin, results, radius }) {
	const map = useMap();
	const prevResultsLen = useRef(0);

	if (results.length > 0 && results.length !== prevResultsLen.current) {
		prevResultsLen.current = results.length;
		const bounds = L.latLngBounds([pin]);
		results.forEach(ad => {
			const coords = ad.geo_location?.coordinates
				? [ad.geo_location.coordinates[1], ad.geo_location.coordinates[0]]
				: ad.location_lat && ad.location_lng ? [ad.location_lat, ad.location_lng] : null;
			if (coords) bounds.extend(coords);
		});
		setTimeout(() => map.fitBounds(bounds.pad(0.15), { maxZoom: 13 }), 100);
	} else if (results.length === 0 && pin && prevResultsLen.current !== 0) {
		prevResultsLen.current = 0;
		setTimeout(() => map.setView(pin, Math.max(8, Math.min(12, 14 - Math.log2(radius)))), 100);
	}

	return null;
}

function DraggableBox({ type, t, pin, radius, setRadius, loading, handleSearch, setPin, geoActive, setGeoActive }) {
	const boxRef = useRef(null);
	const handleRef = useRef(null);
	const [pos, setPos] = useState(null);
	const [geoLoading, setGeoLoading] = useState(false);
	const drag = useRef({ active: false, startX: 0, startY: 0, origX: 0, origY: 0, moved: false, pointerId: null });

	const activateGeoSearch = () => {
		if (!navigator.geolocation) return;
		setGeoLoading(true);
		navigator.geolocation.getCurrentPosition(
			(pos) => {
				const lat = pos.coords.latitude;
				const lng = pos.coords.longitude;
				setPin({ lat, lng });
				setGeoActive(true);
				setGeoLoading(false);
			},
			() => { setGeoLoading(false); },
			{ enableHighAccuracy: true, timeout: 10000 }
		);
	};

	// Clamp coordinates inside the parent container so the box can never disappear off-screen.
	const clamp = (x, y) => {
		const parent = boxRef.current?.parentElement;
		const box = boxRef.current;
		if (!parent || !box) return { x, y };
		const pw = parent.clientWidth;
		const ph = parent.clientHeight;
		const bw = box.offsetWidth;
		const bh = box.offsetHeight;
		return {
			x: Math.max(0, Math.min(x, Math.max(0, pw - bw))),
			y: Math.max(0, Math.min(y, Math.max(0, ph - bh))),
		};
	};

	const onPointerDown = (e) => {
		// Only start drag with primary button / touch / pen
		if (e.button !== undefined && e.button !== 0) return;
		if (!boxRef.current) return;
		e.stopPropagation();
		e.preventDefault();
		const rect = boxRef.current.getBoundingClientRect();
		const parent = boxRef.current.parentElement;
		const parentRect = parent.getBoundingClientRect();
		drag.current = {
			active: true,
			startX: e.clientX,
			startY: e.clientY,
			origX: rect.left - parentRect.left,
			origY: rect.top - parentRect.top,
			moved: false,
			pointerId: e.pointerId,
		};
		try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
	};

	const onPointerMove = (e) => {
		if (!drag.current.active) return;
		const dx = e.clientX - drag.current.startX;
		const dy = e.clientY - drag.current.startY;
		// Only treat as a real drag once movement exceeds a small threshold.
		if (!drag.current.moved && Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
		drag.current.moved = true;
		setPos(clamp(drag.current.origX + dx, drag.current.origY + dy));
	};

	const onPointerUp = (e) => {
		if (!drag.current.active) return;
		drag.current.active = false;
		try { e.currentTarget.releasePointerCapture(drag.current.pointerId); } catch (_) {}
	};

	const style = pos
		? { position: 'absolute', left: pos.x, top: pos.y, zIndex: 49, touchAction: 'none' }
		: { position: 'absolute', top: 16, right: 16, zIndex: 49, touchAction: 'none' };

	return (
		<div ref={boxRef} style={style} className="hidden md:grid bg-white/95 backdrop-blur-sm border border-[#E6E2D6]
		rounded-sm p-4 w-64 shadow-md select-none" data-testid={`map-controls-${type}`}>
			<div
				ref={handleRef}
				onPointerDown={onPointerDown}
				onPointerMove={onPointerMove}
				onPointerUp={onPointerUp}
				onPointerCancel={onPointerUp}
				style={{ touchAction: 'none' }}
				className="flex items-center justify-between cursor-grab active:cursor-grabbing mb-2"
				data-testid={`drag-handle-${type}`}
			>
				<p className="text-xs font-semibold text-[#1A3C34] uppercase tracking-wider pointer-events-none">
					{t('mapSearch.radius')}
				</p>
				<span className="text-[10px] text-[#666055] pointer-events-none">&#x2630;</span>
			</div>
			<Slider
				value={[radius]}
				onValueChange={(v) => setRadius(v[0])}
				min={5} max={500} step={5}
				className="mb-2"
				data-testid={`radius-slider-${type}`}
			/>
			<p className="text-sm text-[#8B4513] font-bold mb-3">{radius} km</p>
			<Button
				onClick={handleSearch}
				disabled={!pin || loading}
				className="w-full bg-[#8B4513] hover:bg-[#6F3610] text-white rounded-sm h-10 text-sm btn-equi"
				data-testid={`search-btn-${type}`}
			>
				<Search className="w-4 h-4 mr-2" />
				{loading ? t('common.loading') : t('mapSearch.search')}
			</Button>
			<Button onClick={ activateGeoSearch } disabled={ geoLoading } variant="outline"
							className={`w-full h-10 rounded-sm text-sm flex items-center gap-2 mt-4 ${geoActive ? 'bg-[#1A3C34] text-white ' +
								'border-[#1A3C34] hover:bg-[#153029]' : 'border-[#E6E2D6] text-[#1A3C34] hover:bg-[#F2F0E9]'}`}>
				<Crosshair className="w-4 h-4" />
				{ geoLoading ?
					t('common.loading') : geoActive ? t('search.nearby_active') : t('search.use_my_location')
				}
			</Button>
			{!pin && <p className="text-[10px] text-[#666055] mt-2 text-center">{t('mapSearch.click_to_pin')}</p>}
		</div>
	);
}

function MapControls({ type, t, pin, radius, setRadius, loading, handleSearch, setPin, geoActive, setGeoActive }) {
	const [geoLoading, setGeoLoading] = useState(false);

	const activateGeoSearch = () => {
		if (!navigator.geolocation) return;
		setGeoLoading(true);
		navigator.geolocation.getCurrentPosition(
			(pos) => {
				const lat = pos.coords.latitude;
				const lng = pos.coords.longitude;
				setPin({ lat, lng });
				setGeoActive(true);
				setGeoLoading(false);
			},
			() => { setGeoLoading(false); },
			{ enableHighAccuracy: true, timeout: 10000 }
		);
	};

	return (
		<div className="grid md:hidden grid-cols-1 bg-white/95 backdrop-blur-sm border border-[#E6E2D6]
				rounded-sm p-4 w-full shadow-md select-none gap-4 mb-4" data-testid={`map-controls-${type}`}>
			<div>
				<div
					className="flex items-center justify-between cursor-grab active:cursor-grabbing mb-2"
					data-testid={`drag-handle-${type}`}>
					<p className="text-xs font-semibold text-[#1A3C34] uppercase tracking-wider pointer-events-none">
						{t('mapSearch.radius')}
					</p>
				</div>
				<Slider
					value={[radius]}
					onValueChange={(v) => setRadius(v[0])}
					min={5} max={500} step={5}
					className="mb-2"
					data-testid={`radius-slider-${type}`}
				/>
				<p className="text-sm text-[#8B4513] font-bold mb-3">{radius} km</p>
			</div>
			<Button
				onClick={handleSearch}
				disabled={!pin || loading}
				className="w-full bg-[#8B4513] hover:bg-[#6F3610] text-white rounded-sm h-10 text-sm btn-equi"
				data-testid={`search-btn-${type}`}
			>
				<Search className="w-4 h-4 mr-2" />
				{loading ? t('common.loading') : t('mapSearch.search')}
			</Button>
			<Button onClick={ activateGeoSearch } disabled={ geoLoading } variant="outline"
							className={`w-full h-10 rounded-sm text-sm flex items-center gap-2 ${geoActive ? 'bg-[#1A3C34] text-white ' +
								'border-[#1A3C34] hover:bg-[#153029]' : 'border-[#E6E2D6] text-[#1A3C34] hover:bg-[#F2F0E9]'}`}>
				<Crosshair className="w-4 h-4" />
				{ geoLoading ?
					t('common.loading') : geoActive ? t('search.nearby_active') : t('search.use_my_location')
				}
			</Button>
			{!pin && <p className="text-[10px] text-[#666055] mt-3 text-center">{t('mapSearch.click_to_pin')}</p>}
		</div>
	);
}

function PinRecenter({ pin, radius }) {
	const map = useMap();
	const did = useRef(false);
	useEffect(() => {
		if (pin && !did.current) {
			did.current = true;
			const z = Math.max(7, Math.min(11, Math.round(13 - Math.log2(radius || 25))));
			setTimeout(() => map.setView([pin.lat, pin.lng], z), 50);
		}
	}, [pin, radius, map]);
	return null;
}

function MapPanel({ type, t, pin, setPin, radius, setRadius }) {
	const [results, setResults] = useState([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);
	const [searched, setSearched] = useState(false);
	const [geoActive, setGeoActive] = useState(false);
	const location = useLocation();
	const initialRanRef = useRef(false);

	const handleMapClick = useCallback((latlng) => {
		setPin(latlng);
		setSearched(false);
	}, [setPin]);

	const runSearch = useCallback(async (p, r) => {
		if (!p) return;
		setLoading(true);
		try {
			const api = resolveApi(type);
			const res = await api.list({ lat: p.lat, lng: p.lng, radius: r, limit: 50 });
			setResults(injectAds(res.data.items || []));
			setTotal(res.data.total || 0);
			setSearched(true);
		} catch {
			setResults([]);
		}
		setLoading(false);
	}, [type]);

	const handleSearch = () => runSearch(pin, radius);

	// Auto-run search on mount if pin came from URL params (back navigation case).
	useEffect(() => {
		if (!initialRanRef.current && pin) {
			initialRanRef.current = true;
			runSearch(pin, radius);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getAdCoords = (ad) => {
		if (ad.geo_location?.coordinates) return { lat: ad.geo_location.coordinates[1], lng: ad.geo_location.coordinates[0] };
		if (ad.location_lat && ad.location_lng) return { lat: ad.location_lat, lng: ad.location_lng };
		return null;
	};

	return (
		<>
			<MapControls type={type} t={t} pin={pin} setPin={setPin} radius={radius} setRadius={setRadius} loading={loading}
									 handleSearch={handleSearch} geoActive={ geoActive } setGeoActive={setGeoActive} />

			<div className="flex md:hidden items-start justify-between mb-2">
				<SaveSearchButton adType="horse" isMobile={ true }/>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-5 gap-6 h-[calc(100vh-220px)] min-h-[500px]">
				{/* Map */}
				<div className="lg:col-span-3 relative rounded-sm overflow-hidden border border-[#E6E2D6]" data-testid={`map-${type}`}>
					<MapContainer center={[39.5, -8.0]} zoom={6} className="h-full w-full min-h-[400px]" style={{ zIndex: 1 }}>
						<TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
						<ClickHandler onMapClick={handleMapClick} />
						<PinRecenter pin={pin} radius={radius} />
						<AutoZoom pin={pin} results={results} radius={radius} />
						{ pin &&
							<Marker position={pin} icon={pinIcon}>
								<Popup>{t('mapSearch.your_pin')}</Popup>
							</Marker>
						}
						{ pin &&
							<Circle center={pin} radius={radius * 1000}
											pathOptions={{ color: '#8B4513', fillColor: '#8B4513', fillOpacity: 0.08, weight: 2 }} />
						}
						{ results.map(ad => {
							const coords = getAdCoords(ad);
							if (!coords) return null;
							return (
								<Marker key={ad.id} position={coords} icon={adIcon}>
									<Popup>
										<Link to={`/${ resolveUrl(type) }/${ad.id}`}
													state={{ from: { path: '/map-search' + location.search, label: t('mapSearch.title') } }}
													className="font-semibold text-[#1A3C34] hover:text-[#8B4513]">
											{ad.title}
										</Link>
										<p className="text-xs text-[#666055]">{ad.price?.toLocaleString()} EUR</p>
										{ad.distance_km != null && <p className="text-xs text-[#8B4513]">{ad.distance_km} km</p>}
									</Popup>
								</Marker>
							);
						})}
					</MapContainer>

					{/* Draggable controls overlay */}
					<DraggableBox type={type} t={t} pin={pin} setPin={setPin} radius={radius} setRadius={setRadius} loading={loading}
												handleSearch={handleSearch} geoActive={ geoActive } setGeoActive={setGeoActive} />
				</div>

				{/* Results panel */}
				<div className="lg:col-span-2 overflow-y-auto" data-testid={`results-panel-${type}`}>
					{!searched && !pin && (
						<div className="flex flex-col items-center justify-center h-full text-center px-4">
							<MapPin className="w-12 h-12 text-[#D4C5A9] mb-4" />
							<p className="text-sm text-[#666055]">{t('mapSearch.empty_state')}</p>
						</div>
					)}
					{!searched && pin && (
						<div className="flex flex-col items-center justify-center h-full text-center px-4">
							<Search className="w-12 h-12 text-[#D4C5A9] mb-4" />
							<p className="text-sm text-[#666055]">{t('mapSearch.pin_set')}</p>
						</div>
					)}
					{searched && (
						<>
							<div className="mb-4 px-1">
								<p className="text-sm font-semibold text-[#1A3C34]">
									{total} {t('mapSearch.results_found')} ({radius} km)
								</p>
							</div>
							{results.length === 0 ? (
								<p className="text-sm text-[#666055] text-center mt-8">{t('mapSearch.no_results')}</p>
							) : (
								<div className="space-y-3">
									{results.map(ad =>
										ad.type === 'listing' ? (
											<Link key={ad.id} to={`/${ resolveUrl(type) }/${ad.id}`}
														state={{ from: { path: '/map-search' + location.search, label: t('mapSearch.title') } }}
														data-testid={`result-card-${ad.id}`}>
												<div className="bg-white border border-[#E6E2D6] rounded-sm p-3 hover:border-[#8B4513]
														transition-colors group">
													<div className="flex gap-3">
														{ ad.images?.[0] && (
															<img
																src={ad.images[0].startsWith('http') ? ad.images[0] : `${process.env.REACT_APP_BACKEND_URL}${ad.images[0]}`}
																alt=""
																className="w-16 h-16 object-cover rounded-sm flex-shrink-0" />
														) }
														<div className="flex-1 min-w-0">
															<h3 className="text-sm font-semibold text-[#1A3C34] truncate group-hover:text-[#8B4513]
																	transition-colors">
																{ad.title}
															</h3>
															<p className="text-xs text-[#666055] truncate">{ad.location_name || ad.breed || ad.category}</p>
															<div className="flex items-center justify-between mt-1">
																<span className="text-sm font-bold text-[#8B4513]">{ad.price?.toLocaleString()} EUR</span>
																{ ad.distance_km != null && (
																	<span className="text-xs text-[#666055] bg-[#F2F0E9] px-2 py-0.5 rounded-sm">
																		{ad.distance_km} km
																	</span>
																)}
															</div>
														</div>
													</div>
												</div>
											</Link>
										) : (
											<AdSlot
												key={ad.id}
												id={ad.id}
												provider="gam"
												placement="search_results_infeed"
											/>
										)
									)}
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</>
	);
}

export default function MapSearchPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();

	const initialTab = resolveTab(searchParams.get('tab'));
	const [activeTab, setActiveTab] = useState(initialTab);

	const latParam = parseFloat(searchParams.get('lat'));
	const lngParam = parseFloat(searchParams.get('lng'));
	const initialPin = !isNaN(latParam) && !isNaN(lngParam) ? { lat: latParam, lng: lngParam } : null;
	const initialRadius = parseInt(searchParams.get('radius') || '25', 10) || 25;

	const [pin, setPinState] = useState(initialPin);
	const [radius, setRadiusState] = useState(initialRadius);

	// Sync state -> URL params (replace to avoid history pollution)
	const updateParams = (next) => {
		const sp = new URLSearchParams(searchParams);
		Object.entries(next).forEach(([k, v]) => {
			if (v === null || v === undefined || v === '') sp.delete(k);
			else sp.set(k, String(v));
		});
		setSearchParams(sp, { replace: true });
	};

	const setPin = (p) => {
		setPinState(p);
		updateParams(p ? { lat: p.lat.toFixed(5), lng: p.lng.toFixed(5) } : { lat: null, lng: null });
	};
	const setRadius = (r) => {
		setRadiusState(r);
		updateParams({ radius: r });
	};
	const switchTab = (tab) => {
		setActiveTab(tab);
		updateParams({ tab });
	};

	return (
		<div className="pt-20 md:pt-24 min-h-screen" data-testid="map-search-page">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
				<h1 className="text-2xl font-medium text-[#1A3C34] font-['Playfair_Display'] mb-3">
					{ t('mapSearch.title') }
				</h1>
				<div className="md:flex items-center justify-between mb-6 gap-3 flex-wrap">
					<div className="space-y-6 gap-3">
						{/* Tabs */}
						<div className="bg-white border border-[#E6E2D6] rounded-sm p-1 gap-1 flex flex-nowrap justify-start
							md:flex-wrap md:justify-center overflow-x-auto md:overflow-visible -mx-4 px-4 md:mx-0 md:px-1
							scrollbar-hide" data-testid="map-tabs">
							<button
								onClick={() => switchTab('horse')}
								className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all 
										${activeTab === 'horse' ? 'bg-[#1A3C34] text-white' : 'text-[#666055] hover:text-[#1A3C34]'}`}
								data-testid="tab-horses"
							>
								{ activeTab === 'horse' &&
									<img src="/favicon-inverted-32x32.png" className="w-6 h-6"/>
								}
								{ activeTab !== 'horse' &&
									<img src="/favicon-32x32.png" className="w-6 h-6"/>
								}
								{t('nav.horses')}
							</button>
							<button
								onClick={() => switchTab('product')}
								className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all whitespace-nowrap
										${activeTab === 'product' ? 'bg-[#1A3C34] text-white' : 'text-[#666055] hover:text-[#1A3C34]'}`}
								data-testid="tab-products"
							>
								<ShoppingBag className="w-4 h-4" /> {t('nav.products')}
							</button>
							<button
								onClick={() => switchTab('transport')}
								className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all whitespace-nowrap
								  	${activeTab === 'transport' ? 'bg-[#1A3C34] text-white' : 'text-[#666055] hover:text-[#1A3C34]'}`}
								data-testid="tab-transports"
							>
								<Truck className="w-4 h-4" /> {t('nav.transports')}
							</button>
							<button
								onClick={() => switchTab('school')}
								className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all whitespace-nowrap
								  	${activeTab === 'school' ? 'bg-[#1A3C34] text-white' : 'text-[#666055] hover:text-[#1A3C34]'}`}
								data-testid="tab-schools"
							>
								<GraduationCap className="w-4 h-4" /> {t('nav.schools')}
							</button>
							<button
								onClick={() => switchTab('stable')}
								className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all whitespace-nowrap
								  	${activeTab === 'stable' ? 'bg-[#1A3C34] text-white' : 'text-[#666055] hover:text-[#1A3C34]'}`}
								data-testid="tab-stables"
							>
								<Home className="w-4 h-4" /> {t('nav.stables')}
							</button>
							<button
								onClick={() => switchTab('trainer')}
								className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all whitespace-nowrap
								  	${activeTab === 'trainer' ? 'bg-[#1A3C34] text-white' : 'text-[#666055] hover:text-[#1A3C34]'}`}
								data-testid="tab-trainers"
							>
								<Award className="w-4 h-4" /> {t('nav.trainers')}
							</button>
							<button
								onClick={() => switchTab('event')}
								className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all whitespace-nowrap
								  	${activeTab === 'event' ? 'bg-[#1A3C34] text-white' : 'text-[#666055] hover:text-[#1A3C34]'}`}
								data-testid="tab-events"
							>
								<CalendarDays className="w-4 h-4" /> {t('nav.events')}
							</button>
							<button
								onClick={() => switchTab('vet')}
								className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all whitespace-nowrap
								  	${activeTab === 'vet' ? 'bg-[#1A3C34] text-white' : 'text-[#666055] hover:text-[#1A3C34]'}`}
								data-testid="tab-vets"
							>
								<Stethoscope className="w-4 h-4" /> {t('nav.vets')}
							</button>
							<SaveSearchButton adType={activeTab} />
						</div>
					</div>
				</div>

				{ activeTab === 'horse' &&
					<MapPanel key="horse" type="horse" t={t} pin={pin} setPin={setPin}
										radius={radius} setRadius={setRadius} />
				}
				{ activeTab === 'product' &&
					<MapPanel key="product" type="product" t={t} pin={pin} setPin={setPin}
										radius={radius} setRadius={setRadius} />
				}
				{ activeTab === 'transport' &&
					<MapPanel key="transport" type="transport" t={t} pin={pin} setPin={setPin}
										radius={radius} setRadius={setRadius} />
				}
				{ activeTab === 'school' &&
					<MapPanel key="school" type="school" t={t} pin={pin} setPin={setPin}
										radius={radius} setRadius={setRadius} />
				}
				{ activeTab === 'stable' &&
					<MapPanel key="stable" type="stable" t={t} pin={pin} setPin={setPin}
										radius={radius} setRadius={setRadius} />
				}
				{ activeTab === 'trainer' &&
					<MapPanel key="trainer" type="trainer" t={t} pin={pin} setPin={setPin}
										radius={radius} setRadius={setRadius} />
				}
				{ activeTab === 'event' &&
					<MapPanel key="event" type="event" t={t} pin={pin} setPin={setPin}
										radius={radius} setRadius={setRadius} />
				}
				{ activeTab === 'vet' &&
					<MapPanel key="vet" type="vet" t={t} pin={pin} setPin={setPin}
										radius={radius} setRadius={setRadius} />
				}
			</div>
		</div>
	);
}